import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  Avatar,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Zoom,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as ArticleIcon,
  Web as WebIcon,
  Psychology as PsychologyIcon,
  LightbulbOutlined as LightbulbIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAppContext } from '../../context/AppContext';

const SAMPLE_QUESTIONS = [
  "What are the risk factors for neonatal jaundice?",
  "How is respiratory distress syndrome diagnosed in premature infants?",
  "What are the current treatment options for necrotizing enterocolitis?",
  "What are the long-term outcomes of hypoxic-ischemic encephalopathy?",
  "How does maternal diabetes affect neonatal outcomes?",
];

const OrchestrationQueryForm = ({ onResultsReceived, onError }) => {
  const theme = useTheme();
  const { selectedStrategy } = useAppContext();

  // Form state
  const [query, setQuery] = useState('');
  const [selectedTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Service selection
  const [useRAG, setUseRAG] = useState(true);
  const [useWebSearch, setUseWebSearch] = useState(true);
  const [useCoreReasoning, setUseCoreReasoning] = useState(true);

  // Advanced parameters
  const [topK, setTopK] = useState(5);
  const [useReasoning, setUseReasoning] = useState(false);
  const [maxWebResults, setMaxWebResults] = useState(5);
  const [reasoningDepth, setReasoningDepth] = useState('standard');

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!query.trim()) {
      setError('Please enter a query.');
      return;
    }

    if (!useRAG && !useWebSearch && !useCoreReasoning) {
      setError('Please select at least one search service.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare services list
      const services = [];
      if (useRAG) services.push('rag');
      if (useWebSearch) services.push('web_search');
      if (useCoreReasoning) services.push('core_reasoning');

      // Prepare service-specific parameters
      const ragParams = {
        top_k: topK,
        topic_ids: selectedTopics.length > 0 ? selectedTopics : null,
        rag_strategy: selectedStrategy,
        use_reasoning: useReasoning
      };

      const webParams = {
        max_results: maxWebResults
      };

      const reasoningParams = {
        domain: 'neonatology',
        reasoning_depth: reasoningDepth
      };

      const orchestrationRequest = {
        query: query.trim(),
        services,
        rag_params: useRAG ? ragParams : {},
        web_params: useWebSearch ? webParams : {},
        reasoning_params: useCoreReasoning ? reasoningParams : {}
      };

      onResultsReceived(orchestrationRequest);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to execute search. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSampleQuestion = (question) => {
    setQuery(question);
    setShowSuggestions(false);
  };

  const getSelectedServicesCount = () => {
    return [useRAG, useWebSearch, useCoreReasoning].filter(Boolean).length;
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const file = new File([blob], 'recording.wav', { type: 'audio/wav' });
        setAudioFile(file);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Could not start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const handleAudioUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAudioFile(file);
    }
  };

  const transcribeAudio = async () => {
    if (!audioFile) return;

    setTranscribing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio_file', audioFile);

      const response = await fetch('/api/transcribe', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();
      setQuery(data.transcription || '');
      setAudioFile(null); // Clear audio file after successful transcription
    } catch (error) {
      console.error('Transcription error:', error);
      setError('Failed to transcribe audio. Please try again.');
    } finally {
      setTranscribing(false);
    }
  };

  const clearAudio = () => {
    setAudioFile(null);
    if (isRecording) {
      stopRecording();
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative gradient bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            mr: 2,
            width: 48,
            height: 48,
            boxShadow: 2,
          }}
        >
          <SearchIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" component="h2" fontWeight="bold">
            Multi-Source Knowledge Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search across internal documents, web sources, and AI reasoning
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Zoom in={true}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            {error}
          </Alert>
        </Zoom>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Query Input */}
        <Box sx={{ position: 'relative', mb: 3 }}>
          <TextField
            fullWidth
            label="Enter your question"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
            multiline
            rows={3}
            placeholder="e.g., What are the risk factors for neonatal jaundice?"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&.Mui-focused': {
                  boxShadow: '0 0 0 3px rgba(63, 81, 181, 0.2)',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <Tooltip title="Get question suggestions">
                  <IconButton
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    color={showSuggestions ? "primary" : "default"}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                    }}
                  >
                    <LightbulbIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />

          {/* Sample Questions */}
          <Collapse in={showSuggestions}>
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Sample Questions:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {SAMPLE_QUESTIONS.map((question, index) => (
                  <Button
                    key={index}
                    variant="text"
                    size="small"
                    onClick={() => handleSampleQuestion(question)}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      borderRadius: 1,
                    }}
                  >
                    {question}
                  </Button>
                ))}
              </Box>
            </Paper>
          </Collapse>
        </Box>

        {/* Audio Input Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Audio Input
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Recording Button */}
            <Button
              variant={isRecording ? "contained" : "outlined"}
              color={isRecording ? "error" : "primary"}
              startIcon={isRecording ? <StopIcon /> : <MicIcon />}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={transcribing}
              sx={{ borderRadius: 2 }}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </Button>

            {/* File Upload */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={transcribing || isRecording}
              sx={{ borderRadius: 2 }}
            >
              Upload Audio
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioUpload}
                style={{ display: 'none' }}
              />
            </Button>

            {/* Audio File Status */}
            {audioFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`Audio: ${audioFile.name}`}
                  onDelete={clearAudio}
                  color="info"
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={transcribeAudio}
                  disabled={transcribing}
                  sx={{ borderRadius: 2 }}
                >
                  {transcribing ? 'Transcribing...' : 'Transcribe'}
                </Button>
              </Box>
            )}
          </Box>

          {transcribing && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Transcribing audio...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Service Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Search Sources
            <Chip 
              label={`${getSelectedServicesCount()} selected`} 
              size="small" 
              color="primary"
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useRAG}
                  onChange={(e) => setUseRAG(e.target.checked)}
                  icon={<ArticleIcon />}
                  checkedIcon={<ArticleIcon />}
                />
              }
              label="Internal Documents (RAG)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useWebSearch}
                  onChange={(e) => setUseWebSearch(e.target.checked)}
                  icon={<WebIcon />}
                  checkedIcon={<WebIcon />}
                />
              }
              label="Web Search"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCoreReasoning}
                  onChange={(e) => setUseCoreReasoning(e.target.checked)}
                  icon={<PsychologyIcon />}
                  checkedIcon={<PsychologyIcon />}
                />
              }
              label="AI Reasoning"
            />
          </FormGroup>
        </Box>

        {/* Advanced Settings */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<SettingsIcon />}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAdvanced(!showAdvanced)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2 }}
          >
            Advanced Settings
          </Button>

          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              {/* RAG Settings */}
              {useRAG && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Internal Documents Settings
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption">Results Count</Typography>
                      <Slider
                        value={topK}
                        onChange={(e, value) => setTopK(value)}
                        min={1}
                        max={20}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useReasoning}
                          onChange={(e) => setUseReasoning(e.target.checked)}
                        />
                      }
                      label="Use Reasoning Loop"
                    />
                  </Box>
                </Box>
              )}

              {/* Web Search Settings */}
              {useWebSearch && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Web Search Settings
                  </Typography>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption">Max Results</Typography>
                    <Slider
                      value={maxWebResults}
                      onChange={(e, value) => setMaxWebResults(value)}
                      min={1}
                      max={10}
                      marks
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Box>
              )}

              {/* Reasoning Settings */}
              {useCoreReasoning && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    AI Reasoning Settings
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Reasoning Depth</InputLabel>
                    <Select
                      value={reasoningDepth}
                      onChange={(e) => setReasoningDepth(e.target.value)}
                      label="Reasoning Depth"
                    >
                      <MenuItem value="concise">Concise</MenuItem>
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="detailed">Detailed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading || !query.trim() || getSelectedServicesCount() === 0}
          startIcon={<SearchIcon />}
          sx={{
            borderRadius: 3,
            py: 1.5,
            px: 4,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {loading ? 'Searching...' : 'Search All Sources'}
        </Button>
      </Box>
    </Paper>
  );
};

export default OrchestrationQueryForm;
